import classNames from "classnames";
import React, {
  isValidElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { LinkConstructor, LinkProps } from "../../types";
import { CloseIcon, IconComponent, MenuIcon } from "../Icon/Icon";
import { Logo } from "../Logo/Logo";
import { useVSETHContext } from "../VSETHContext/VSETHContext";
import { ActionItem } from "./ActionItem";
import { globalNav } from "./GlobalNav";
import { ItemComponent } from "./ItemComponent";

interface LangMap {
  en: ReactNode;
  [s: string]: ReactNode;
}

export interface Item {
  /**
   * Title, either string or map with key: language, value: translation, please always provide english
   */
  title?: ReactNode | LangMap;
  /**
   * Set active state
   */
  active?: boolean;
  /**
   * Set disabled state
   */
  disabled?: boolean;
  /**
   * Make a dropdown out of it
   */
  childItems?: Item[];
  /**
   * The svg path for the Icon component. If a sencodary NavItem has an iocn defined, it well be shown in mobile in the blue navbar at the bottom
   */
  icon?: IconComponent;
  /**
   * Set href for link
   */
  href?: string;
  /**
   * Set onClick
   */
  onClick?: any;
  /**
   * this gets passed to the react router Link Component
   */
  // linkProps?: LinkProps; TODO
  linkProps?: LinkProps;
}

export interface NavbarConfig {
  primaryLogo?: ReactNode;
  primarySignet?: ReactNode;
  primaryItems?: Item[];
  secondaryNavItems?: Item[];
  primaryActionItems?: Item[];
  primarySlot?: ReactNode;
  secondarySlot?: ReactNode;
  mobileTopSlot?: ReactNode;
  drawerSlot?: ReactNode;
  secondaryLogo?: ReactNode;
  secondarySignet?: ReactNode;
  lang?: string;
  NavLink: LinkConstructor;
}
function isLangMap(obj: LangMap | {}): obj is LangMap {
  return obj.hasOwnProperty("en");
}
function getTitle(title: ReactNode | LangMap, lang: string) {
  if (title === undefined || title === null) return undefined;
  if (
    typeof title === "string" ||
    typeof title === "number" ||
    typeof title === "boolean" ||
    Array.isArray(title)
  )
    return title;
  if (isValidElement(title)) {
    return title;
  }
  if (isLangMap(title)) {
    return title[lang] ?? title.en;
  }
  return undefined;
}
/**
 *
 * @param items List of Items
 * @param lang language code (eg. "en" or "de")
 *
 * Takes an Item array and returns an Item array where every title is in the target language if possible, defaults to "en" if unavailable
 */
function translate(items: Item[], lang: string): Item[] {
  return items.map(item => ({
    ...item,
    title: getTitle(item.title, lang),
    childItems: item.childItems
      ? translate(item.childItems, lang)
      : item.childItems,
  }));
}

export const VSETHNavbar: React.FC<NavbarConfig> = ({
  primaryLogo,
  primarySignet = <Logo height={32} variant="signet" />,
  primaryItems,
  primaryActionItems = [],
  primarySlot = null,
  secondarySlot = null,
  mobileTopSlot = null,
  drawerSlot = null,
  secondaryNavItems = [],
  secondaryLogo = null,
  secondarySignet = null,
  lang = "en",
  NavLink,
}) => {
  const { globID, static_url } = useVSETHContext();
  const navbarURL = `${static_url}/assets/${globID}/navbar-extern.json`;
  const navbarConfigKey = `@vseth/navbar_config-${globID}`;
  const [expanded, setExpanded] = useState(false);

  // Get saved config from store, if unavailable, default to GlobalNav.ts
  let storedPrimaryItems: string | null | undefined = useMemo(
    () =>
      typeof window !== "undefined"
        ? localStorage.getItem(navbarConfigKey)
        : undefined,
    [navbarConfigKey]
  );
  const [externalPrimaryItems, setExternalPrimaryItems] = useState(
    storedPrimaryItems ? (JSON.parse(storedPrimaryItems) as Item[]) : globalNav
  );

  // Try to load new Navbar config, save and replace it with new one if it changed
  useEffect(() => {
    let cancel = false;
    async function fetchData() {
      if (typeof window !== "undefined") {
        const response = await fetch(navbarURL);
        if (cancel) return;
        const navConfig = await response.json();
        if (cancel) return;
        const stringified = JSON.stringify(navConfig);
        if (stringified !== storedPrimaryItems) {
          localStorage.setItem(navbarConfigKey, stringified);
          setExternalPrimaryItems(navConfig);
        }
      }
    }
    fetchData();
    return () => {
      cancel = true;
    };
  }, [navbarConfigKey, navbarURL]);

  // Need to process all objects as title values to the localised string

  const processedPrimaryItems = useMemo(
    () => translate(primaryItems ?? externalPrimaryItems, lang),
    [lang, primaryItems, externalPrimaryItems]
  );

  const primaryItemsComponent = useMemo(
    () =>
      processedPrimaryItems.map((item, i) => (
        <ItemComponent
          item={{ ...item, icon: undefined }}
          key={i}
          NavLink={NavLink}
        />
      )),
    [processedPrimaryItems, NavLink]
  );

  return (
    <>
      {/* Mobile header */}
      <header className="mobile-header bg-white static-top">
        <div className="container d-flex">
          <div className="brand split-brand">
            {primarySignet}
            {secondarySignet && <div className="split" />}
            {secondarySignet}
          </div>
          {mobileTopSlot}
        </div>
      </header>
      {/* Global header (primary color) */}
      <nav className="navbar navbar-expand-lg navbar-dark navbar-global bg-primary static-top ">
        <div className="container">
          {primaryLogo ?? (
            <a className="navbar-brand white-brand" href="#">
              <Logo size={30} variant="logo-mono" />
            </a>
          )}
          <div className="collapse navbar-collapse" id="navbarDisappear">
            <ul className="navbar-nav ml-auto">{primaryItemsComponent}</ul>
            <ul className="navbar-icon-row ">
              {primaryActionItems.map((item, i) => (
                <ActionItem item={item} key={i} NavLink={NavLink} />
              ))}
            </ul>
            {primarySlot}
          </div>
        </div>
      </nav>
      {/* Page navbar (white color) */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white sticky-top">
        <div className="container">
          {secondaryLogo}
          {/* Icons for mobile navbar */}
          <div className="mobile-nav-container">
            <div className="navbar-mobile">
              <ul className="navbar-mobile-nav light-icon">
                {/* Filter out all icons that have no icon and remove title*/}
                {secondaryNavItems.map((item, i) =>
                  item.icon ? (
                    <ItemComponent
                      item={{ ...item, title: undefined }}
                      key={i}
                      NavLink={NavLink}
                    />
                  ) : null
                )}
              </ul>
            </div>
            <button
              className="navbar-toggler light-icon collapsed"
              type="button"
              id="navbarToggler"
              onClick={() => setExpanded(!expanded)}
              aria-controls="navbarResponsive"
              aria-expanded={expanded}
              aria-label="Toggle navigation"
            >
              {expanded ? (
                <CloseIcon className="navbar-toggler-icon" />
              ) : (
                <MenuIcon className="navbar-toggler-icon" />
              )}
            </button>
          </div>
          {/* Navbar text nav (non-mobile nav) */}
          <div className="collapse navbar-collapse" id="navbarResponsive_">
            <ul className="navbar-nav ml-auto">
              {secondaryNavItems.map((item, i) => (
                <ItemComponent
                  key={i}
                  item={{ ...item, icon: undefined }}
                  NavLink={NavLink}
                />
              ))}
            </ul>
            {secondarySlot}
          </div>
        </div>
      </nav>
      {/* Mobile drawer */}
      <div
        className={classNames("collapse nav-drawer", { show: expanded })}
        id="navbarResponsive"
      >
        <div className="navbar-collapse">
          <ul className="navbar-nav">
            {secondaryNavItems.map((item, i) => (
              <ItemComponent
                key={i}
                item={{ ...item, icon: undefined }}
                NavLink={NavLink}
              />
            ))}
          </ul>
        </div>
        <div className="absolute-bottom">
          <ul className="navbar-icon-row ">
            {primaryActionItems.map((item, i) => (
              <ActionItem key={i} item={item} NavLink={NavLink} />
            ))}
          </ul>
          {drawerSlot}
        </div>
      </div>
      {/* Add some space in top */}
      <div style={{ height: 40 }} />
    </>
  );
};
